import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const ROLES = ['owner', 'admin', 'manager', 'technician', 'salesperson', 'viewer'];

const CompanyUsers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: '', email: '', role: 'technician' });
  const [subscription, setSubscription] = useState(null);
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, subRes] = await Promise.all([
        supabase?.from('org_members')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('org_subscriptions')?.select('*')?.limit(1)?.single()
      ]);
      setMembers(membersRes?.data || []);
      if (subRes?.data) setSubscription(subRes?.data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm?.email?.trim()) { setError('Email is required.'); return; }
    if (!inviteForm?.full_name?.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const orgRes = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
      if (!orgRes?.data?.id) throw new Error('No organization found. Please set up your organization first.');

      const activeCount = members?.filter(m => m?.is_active)?.length;
      const maxUsers = subscription?.max_users || 5;
      if (activeCount >= maxUsers) {
        throw new Error(`User limit reached (${maxUsers}). Upgrade your plan to add more users.`);
      }

      const { error: err } = await supabase?.from('org_members')?.insert({
        organization_id: orgRes?.data?.id,
        email: inviteForm?.email?.trim()?.toLowerCase(),
        full_name: inviteForm?.full_name?.trim(),
        role: inviteForm?.role,
        is_active: true,
        invited_at: new Date()?.toISOString()
      });
      if (err) throw err;
      setSuccess(`${inviteForm?.full_name} has been added successfully!`);
      setInviteForm({ full_name: '', email: '', role: 'technician' });
      setShowInviteForm(false);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to add user.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const { error: err } = await supabase?.from('org_members')?.update({ role: newRole, updated_at: new Date()?.toISOString() })?.eq('id', memberId);
      if (err) throw err;
      setMembers(prev => prev?.map(m => m?.id === memberId ? { ...m, role: newRole } : m));
      setEditingMember(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleToggleActive = async (member) => {
    try {
      const { error: err } = await supabase?.from('org_members')?.update({
        is_active: !member?.is_active,
        updated_at: new Date()?.toISOString()
      })?.eq('id', member?.id);
      if (err) throw err;
      setMembers(prev => prev?.map(m => m?.id === member?.id ? { ...m, is_active: !m?.is_active } : m));
    } catch (err) {
      setError(err?.message);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-red-100 text-red-700',
      manager: 'bg-blue-100 text-blue-700',
      technician: 'bg-green-100 text-green-700',
      salesperson: 'bg-yellow-100 text-yellow-700',
      viewer: 'bg-gray-100 text-gray-600'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-600';
  };

  const activeCount = members?.filter(m => m?.is_active)?.length;
  const maxUsers = subscription?.max_users || 5;

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Company Users - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-6 pb-16 px-4 max-w-4xl mx-auto">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Users" size={22} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Company Users</h1>
              <p className="text-sm text-muted-foreground">{activeCount} of {maxUsers} users active</p>
            </div>
          </div>
          <button
            onClick={() => { setShowInviteForm(true); setError(''); setSuccess(''); }}
            disabled={activeCount >= maxUsers}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Icon name="UserPlus" size={16} color="currentColor" />
            Add User
          </button>
        </div>

        {/* Usage Bar */}
        <div className="bg-card rounded-xl border border-border p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">User Seats Used</span>
            <span className="text-sm text-muted-foreground">{activeCount} / {maxUsers}</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                activeCount >= maxUsers ? 'bg-red-500' : activeCount >= maxUsers * 0.8 ? 'bg-yellow-500' : 'bg-primary'
              }`}
              style={{ width: `${Math.min(100, (activeCount / maxUsers) * 100)}%` }}
            />
          </div>
          {activeCount >= maxUsers && (
            <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} color="currentColor" />
              User limit reached. Upgrade your plan to add more users.
            </p>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-4">
            <Icon name="AlertCircle" size={16} color="#ef4444" />
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={() => setError('')} className="ml-auto"><Icon name="X" size={14} color="#ef4444" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl mb-4">
            <Icon name="CheckCircle" size={16} color="#16a34a" />
            <p className="text-sm text-green-700">{success}</p>
            <button onClick={() => setSuccess('')} className="ml-auto"><Icon name="X" size={14} color="#16a34a" /></button>
          </div>
        )}

        {/* Invite Form */}
        {showInviteForm && (
          <div className="bg-card rounded-xl border border-border p-5 mb-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="UserPlus" size={18} color="var(--color-primary)" />
              Add New User
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                <input
                  type="text"
                  value={inviteForm?.full_name}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, full_name: e?.target?.value }))}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                <input
                  type="email"
                  value={inviteForm?.email}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, email: e?.target?.value }))}
                  placeholder="user@company.com"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
                <select
                  value={inviteForm?.role}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, role: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ROLES?.map(r => (
                    <option key={r} value={r} className="capitalize">{r?.charAt(0)?.toUpperCase() + r?.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleInvite}
                disabled={saving}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="UserPlus" size={14} color="currentColor" />}
                Add User
              </button>
              <button
                onClick={() => { setShowInviteForm(false); setError(''); }}
                className="px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Members Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members?.length === 0 ? (
          <div className="bg-card rounded-xl border border-border p-12 text-center">
            <Icon name="Users" size={48} color="#9ca3af" />
            <p className="mt-3 font-medium text-foreground">No users yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add your first team member to get started</p>
          </div>
        ) : (
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Added</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members?.map(member => (
                    <tr key={member?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-primary">
                              {member?.full_name?.charAt(0)?.toUpperCase() || member?.email?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{member?.full_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{member?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingMember === member?.id ? (
                          <select
                            defaultValue={member?.role}
                            onChange={(e) => handleUpdateRole(member?.id, e?.target?.value)}
                            onBlur={() => setEditingMember(null)}
                            autoFocus
                            className="px-2 py-1 border border-border rounded text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            {ROLES?.map(r => (
                              <option key={r} value={r} className="capitalize">{r?.charAt(0)?.toUpperCase() + r?.slice(1)}</option>
                            ))}
                          </select>
                        ) : (
                          <button
                            onClick={() => setEditingMember(member?.id)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize hover:opacity-80 transition-opacity ${getRoleColor(member?.role)}`}
                          >
                            {member?.role}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          member?.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {member?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {member?.invited_at ? new Date(member?.invited_at)?.toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleToggleActive(member)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                            member?.is_active
                              ? 'bg-red-50 text-red-600 hover:bg-red-100' :'bg-green-50 text-green-600 hover:bg-green-100'
                          }`}
                        >
                          {member?.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompanyUsers;
